import { api_json, ApiError, get_api_error_message } from './api'
import { api_error_code } from './api-error-code'
import { CaptchaUnavailableError, captcha_unavailable_message, get_turnstile_token } from './turnstile'
import { escape_html } from './html'
import { icon } from './icons'
import { format_cents } from './format'
import type { ApiFailure } from './api-failure'
import { carregar_colecao } from './collection-load'
import {

  open_multi_select_modal,
  open_single_select_modal,
  type ComboOption
} from './combobox-modal'
/**
 * Espelho da regra do backend (`assert_contact_channel`, decisão 4 da F5.1).
 *
 * Duplicação consciente e mínima: a UI precisa saber a regra para não empurrar o
 * anunciante contra um 400. O backend continua sendo quem decide — isto só
 * antecipa a mensagem.
 */
/**
 * Mensagem de erro do envio público.
 *
 * Extraída do handler porque era um ternário aninhado de cinco níveis dentro de
 * um `catch` — impossível de testar e fácil de errar.
 *
 * O caso que motiva o cuidado: **quota e rate limiter respondem os dois 429**, e
 * são coisas diferentes. Quem estourou a quota precisa ouvir "volte daqui a
 * alguns dias"; quem bateu no limiter, "aguarde alguns instantes". A tela
 * decidia pelo STATUS e mandava o primeiro tentar de novo para sempre, sem
 * nunca dizer o motivo real. Quem decide é o CÓDIGO.
 */
export function submit_error_message(err: unknown): string {
  // Indisponibilidade do CAPTCHA (config que não veio, script bloqueado por
  // adblock) é diferente de desafio não concluído. O primeiro não é culpa de
  // quem está enviando e não pede recarregar a página — pede tentar de novo.
  if (err instanceof CaptchaUnavailableError) return captcha_unavailable_message()
  if (err instanceof Error && err.message.startsWith('turnstile_')) {
    return 'A verificação de segurança não foi concluída e nada foi enviado. Tente enviar de novo.'
  }
  if (api_error_code(err) === 'quota_exceeded') {
    // A mensagem do servidor traz o teto e a janela; um texto fixo aqui perderia
    // justamente a saída.
    return get_api_error_message(err, 'Você atingiu o limite de anúncios por período.')
  }
  if (err instanceof ApiError && err.status === 429) return 'Muitos envios em sequência. Aguarde alguns instantes.'
  return get_api_error_message(err, 'Não foi possível enviar seu anúncio.')
}

export function tem_canal_de_contato(email: string, phone: string): boolean {
  const e = String(email || '').trim()
  const email_ok = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(e)
  // Mesmo critério do `normalize_phone`: 10 ou 11 dígitos, com o 55 do país
  // saindo só quando sobra um número nacional válido.
  const digitos = String(phone || '').replace(/\D/g, '')
  const nacional =
    digitos.startsWith('55') && (digitos.length === 12 || digitos.length === 13) ? digitos.slice(2) : digitos
  const phone_ok = nacional.length === 10 || nacional.length === 11
  return email_ok || phone_ok
}


export type Category = { id: number; code: string; label: string; prefix: string; position?: number }
export type AdType = { id: number; code: string; label: string; label_plural?: string | null; position?: number }
export type Subcategory = { id: number; category_id: number; code: string; label: string; position?: number }
export type PricingRate = {
  category_id: number | null
  type_id: number | null
  title_cents: number
  body_cents: number
}
export type Pricing = {
  mode: 'free' | 'paid'
  rates: PricingRate[]
  default: { title_cents: number; body_cents: number } | null
}
export type StateRow = { id: number; code: string; name: string }
export type Municipality = { id: number; state_id: number; ibge_code: number | null; name: string; state_code: string }
export type DistrictRow = { id: number; municipality_id: number; name: string; source: string }
export type GeoIp = {
  country: string | null
  region_code: string | null
  region_name: string | null
  city: string | null
  neighborhood?: string | null
  source: string
}

export type SubmitResult = {
  ok: boolean
  idempotent: boolean
  short_code: string
  owner_token: string | null
  estimated_cost_cents: number
  estimated_cost_title_cents: number
  estimated_cost_body_cents: number
  title_letter_count: number
  body_letter_count: number
}

type FormState = {
  category: Category | null
  ad_type: AdType | null
  subcategories: Subcategory[]
  state: StateRow | null
  municipality: Municipality | null
  neighborhood: string
}

function letter_count_of(text: string): number {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]/g, '').length
}

function resolve_rate(pricing: Pricing | null, category_id: number | null, type_id: number | null): { title_cents: number; body_cents: number } {
  if (!pricing || !pricing.rates?.length) return { title_cents: 0, body_cents: 0 }
  const exact = pricing.rates.find(r => r.category_id === category_id && r.type_id === type_id)
  if (exact) return { title_cents: exact.title_cents, body_cents: exact.body_cents }
  const cat = pricing.rates.find(r => r.category_id === category_id && r.type_id == null)
  if (cat) return { title_cents: cat.title_cents, body_cents: cat.body_cents }
  const type_only = pricing.rates.find(r => r.category_id == null && r.type_id === type_id)
  if (type_only) return { title_cents: type_only.title_cents, body_cents: type_only.body_cents }
  const def = pricing.rates.find(r => r.category_id == null && r.type_id == null)
  if (def) return { title_cents: def.title_cents, body_cents: def.body_cents }
  if (pricing.default) return pricing.default
  return { title_cents: 0, body_cents: 0 }
}

export function submit_form_html(opts: { variant: 'page' | 'modal' } = { variant: 'page' }): string {
  const cancel_btn = opts.variant === 'modal'
    ? `<button type="button" data-submit-cancel class="inline-flex h-10 items-center rounded-md border border-border bg-surface px-4 text-xs font-bold uppercase tracking-wide text-ink hover:bg-surface2">Cancelar</button>`
    : `<a href="/" class="inline-flex h-10 items-center rounded-md border border-border bg-surface px-4 text-xs font-bold uppercase tracking-wide text-ink hover:bg-surface2">Cancelar</a>`

  return `
    <form data-submit-form class="grid gap-4">
      <div class="grid gap-3 sm:grid-cols-2">
        <div class="grid gap-1 text-sm">
          <span class="text-xs font-semibold uppercase tracking-wider text-muted">Categoria</span>
          <button type="button" data-trigger-category class="js-trigger flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-ink hover:bg-surface2">
            <span data-trigger-label class="truncate text-muted">Escolha a categoria…</span>
            ${icon('chevron-down', 'h-4 w-4 text-muted')}
          </button>
        </div>
        <div class="grid gap-1 text-sm">
          <span class="text-xs font-semibold uppercase tracking-wider text-muted">Tipo de anúncio</span>
          <button type="button" data-trigger-type class="js-trigger flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-ink hover:bg-surface2">
            <span data-trigger-label class="truncate text-muted">Oferta ou procura?</span>
            ${icon('chevron-down', 'h-4 w-4 text-muted')}
          </button>
        </div>
      </div>

      <div class="grid gap-1 text-sm">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Subcategorias (opcional, múltipla)</span>
        <button type="button" data-trigger-subcategories class="js-trigger flex min-h-10 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-ink hover:bg-surface2 py-1.5">
          <span data-subcategory-chips class="flex flex-1 flex-wrap items-center gap-1.5 py-0.5"></span>
          ${icon('chevron-down', 'h-4 w-4 text-muted shrink-0')}
        </button>
        <span class="text-[11px] text-muted">Selecione uma ou mais opções já cadastradas pelo admin.</span>
      </div>

      <label class="grid gap-1 text-sm">
        <span class="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted">
          <span>Título do anúncio</span>
          <span data-title-letters class="font-mono normal-case tracking-normal text-muted">0 letras</span>
        </span>
        <input name="title" required minlength="4" maxlength="160" placeholder="Frase curta e direta" class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none" />
      </label>

      <label class="grid gap-1 text-sm">
        <span class="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted">
          <span>Descrição completa</span>
          <span data-body-letters class="font-mono normal-case tracking-normal text-muted">0 letras</span>
        </span>
        <textarea name="body" required minlength="10" maxlength="2000" rows="6" placeholder="Detalhe seu anúncio: condição, preço, localização, contato preferido…" class="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink outline-none"></textarea>
      </label>

      <div class="grid gap-3 sm:grid-cols-3">
        <label class="grid gap-1 text-sm">
          <span class="text-xs font-semibold uppercase tracking-wider text-muted">UF (opcional)</span>
          <select name="state_uf" data-state-select class="h-10 rounded-md border border-border bg-surface px-3 text-sm uppercase text-ink outline-none">
            <option value="">Carregando…</option>
          </select>
        </label>
        <div class="grid gap-1 text-sm sm:col-span-2">
          <span class="text-xs font-semibold uppercase tracking-wider text-muted">Cidade (opcional)</span>
          <button type="button" data-trigger-municipality disabled class="js-trigger flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-ink hover:bg-surface2 disabled:opacity-50 disabled:cursor-not-allowed">
            <span data-trigger-label class="truncate text-muted">Selecione a UF para buscar cidade…</span>
            ${icon('chevron-down', 'h-4 w-4 text-muted')}
          </button>
        </div>
      </div>

      <div class="grid gap-1 text-sm">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Bairro (opcional)</span>
        <button type="button" data-trigger-neighborhood disabled class="js-trigger flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm text-ink hover:bg-surface2 disabled:opacity-50 disabled:cursor-not-allowed">
          <span data-trigger-label class="truncate text-muted">Selecione primeiro a cidade…</span>
          ${icon('chevron-down', 'h-4 w-4 text-muted')}
        </button>
        <span class="text-[11px] text-muted">Busque na lista ou crie um bairro novo digitando e pressionando Enter.</span>
      </div>

      <label class="grid gap-1 text-sm">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">Preço do bem (opcional)</span>
        <input name="price_brl" type="text" inputmode="decimal" placeholder="Ex.: 1500,00" class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none" />
        <span class="text-[11px] text-muted">Deixe em branco para "a combinar". Em reais.</span>
      </label>

      <fieldset class="grid gap-3 rounded-md border border-border bg-surface2 p-4 text-sm">
        <legend class="px-1 text-xs font-semibold uppercase tracking-wider text-muted">Fotos do anúncio (opcional)</legend>
        <input data-photo-input type="file" accept="image/jpeg,image/png,image/webp" multiple class="sr-only" />
        <button type="button" data-photo-pick class="grid place-items-center gap-1 rounded-md border border-dashed border-border bg-surface px-4 py-5 text-sm text-muted transition-colors hover:border-brand hover:text-ink" aria-label="Adicionar fotos do anúncio">
          ${icon('camera', 'h-6 w-6')}
          <span class="font-semibold text-ink">Adicionar fotos</span>
          <span data-photo-counter class="text-[11px]">0 de 6 · arraste ou toque para escolher</span>
        </button>
        <div data-photo-grid class="hidden grid-cols-3 gap-2 sm:grid-cols-6"></div>
        <span class="text-[11px] text-muted">Até 6 fotos de 10 MB (JPEG, PNG ou WebP). A primeira é a capa. As fotos passam pela moderação junto com o texto e são publicadas sem dados de localização do arquivo.</span>
      </fieldset>

      <fieldset class="grid gap-3 rounded-md border border-border bg-surface2 p-4 text-sm">
        <legend class="px-1 text-xs font-semibold uppercase tracking-wider text-muted">Contato</legend>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1">
            <span class="text-xs font-semibold uppercase tracking-wider text-muted">Seu nome</span>
            <input name="contact_name" maxlength="120" required class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none" />
          </label>
          <label class="grid gap-1">
            <span class="text-xs font-semibold uppercase tracking-wider text-muted">WhatsApp / telefone</span>
            <input name="contact_phone" maxlength="40" placeholder="(61) 9XXXX-XXXX" class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none" />
          </label>
        </div>
        <label class="grid gap-1">
          <span class="text-xs font-semibold uppercase tracking-wider text-muted">Email</span>
          <input name="contact_email" type="email" maxlength="255" class="h-10 rounded-md border border-border bg-surface px-3 text-sm text-ink outline-none" />
          <span class="text-[11px] text-muted">O link de gestão aparece ao final do envio.</span>
        </label>
        <!--
          Decisão 4 (F5.1): telefone OU email é obrigatório. Os dois campos diziam
          "(opcional)" e o anunciante descobriria a exigência levando 400 no envio
          — a UI reflete a MESMA regra do backend, e a mensagem diz qual é.
        -->
        <p class="js-contact-hint rounded-md border-l-4 border-l-brand bg-surface2 px-3 py-2 text-sm text-muted">
          Informe pelo menos um: telefone com DDD ou email. É por onde os interessados vão falar com você.
        </p>
        <!--
          Consentimento (F8.2). O aceite fica registrado com a VERSÃO do texto que
          estava no ar neste envio — dizer só "você aceitou" permitiria reescrever
          os termos depois e alegar que a pessoa concordou com o texto novo.
        -->
        <p class="text-sm text-muted">
          Ao enviar, você concorda com os
          <a href="/termos" target="_blank" rel="noopener" class="font-semibold text-brand underline">termos de uso</a>
          e a
          <a href="/privacidade" target="_blank" rel="noopener" class="font-semibold text-brand underline">política de privacidade</a>.
        </p>
        <label class="flex items-center gap-2 text-sm text-ink">
          <input name="contact_form_enabled" type="checkbox" checked class="h-4 w-4" />
          <span>Aceitar mensagens pelo formulário de contato do anúncio.</span>
        </label>
      </fieldset>

      <div data-submit-status class="hidden rounded-md border px-3 py-2 text-sm"></div>

      <div data-submit-actions class="border-t border-border pt-4">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="text-sm min-w-0">
            <div data-cost-total class="font-mono text-lg font-bold text-ink">R$ 0,00 · gratuito nesta fase</div>
            <div data-cost-detail class="text-[11px] text-muted">Submissão moderada · link de gestão aparece após o envio</div>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            ${cancel_btn}
            <button type="submit" data-submit-button class="inline-flex h-10 items-center gap-2 rounded-md bg-brand px-4 text-xs font-bold uppercase tracking-wide text-brand-ink hover:opacity-90">${icon('plus', 'h-4 w-4')} Enviar para moderação</button>
          </div>
        </div>
      </div>
    </form>`
}

export function submit_success_html(short_code: string, owner_token: string | null): string {
  const link = `/minha-conta?token=${encodeURIComponent(owner_token || '')}`
  return `
    <article class="rounded-md border border-ok/40 bg-ok/10 p-6 shadow-card">
      <div class="flex items-center gap-3">
        <span class="grid h-10 w-10 place-items-center rounded-full bg-ok text-ok-ink">${icon('check', 'h-5 w-5')}</span>
        <div>
          <h1 class="font-serif text-2xl font-bold text-ink">Anúncio enviado para moderação</h1>
          <p class="text-sm text-muted">Identificador: <strong class="font-mono text-ink">${escape_html(short_code)}</strong></p>
        </div>
      </div>

      ${owner_token
        ? `<div class="mt-5 rounded-md border border-warning bg-warning/10 p-4 text-sm text-ink">
            <div class="font-semibold uppercase tracking-wide text-xs text-warning">Token de gestão · guarde agora</div>
            <p class="mt-1 text-sm text-ink">
              Este token só é exibido uma vez. Use para editar, desativar ou acompanhar o seu anúncio.
            </p>
            <code class="mt-3 block break-all rounded-md bg-surface px-3 py-2 font-mono text-xs text-ink">${escape_html(owner_token)}</code>
            <a href="${link}" class="mt-3 inline-flex h-9 items-center gap-2 rounded-md bg-ink px-3 text-xs font-bold uppercase tracking-wide text-canvas">${icon('edit', 'h-4 w-4')} Abrir minha conta</a>
          </div>`
        : `<p class="mt-4 text-sm text-muted">Já existia um anúncio com a mesma identificação. Recuperação não retornou novo token.</p>`}

      <div class="mt-5 grid gap-2 text-sm text-muted">
        <p>Um administrador irá aprovar, rejeitar ou pedir alterações antes do anúncio aparecer publicamente.</p>
        <p>Use o link de gestão para acompanhar, editar enquanto estiver em moderação ou desativar o anúncio.</p>
      </div>
    </article>`
}

export type SubmitDependencies = {
  categories: Category[]
  ad_types: AdType[]
  pricing: Pricing | null
  subcategories: Subcategory[]
  states: StateRow[]
  geoip: GeoIp | null
  /** Preenchido quando categoria ou tipo falharam — sem eles não há formulário. */
  failure: ApiFailure | null
}

/**
 * Nem toda dependência do formulário tem o mesmo peso.
 *
 * Sem **categoria** e sem **tipo** o formulário não pode ser preenchido: o
 * `<select>` sai vazio e o anunciante não tem como enviar nada. Isso não é
 * degradação, é tela quebrada — e o `.catch(() => ({ items: [] }))` a fazia
 * parecer uma tela normal sem opções.
 *
 * Preço, subcategoria, UF e geolocalização degradam de verdade: o formulário
 * funciona sem eles (`pricing` e `geoip` já são nulos por contrato). Esses
 * ficam registrados no console e seguem.
 */
export async function fetch_submit_dependencies(): Promise<SubmitDependencies> {
  const [cats, types, pricing, subs, states, geoip] = await Promise.all([
    carregar_colecao('as categorias', () => api_json<{ items: Category[] }>('/api/public/categories')),
    carregar_colecao('os tipos de anúncio', () => api_json<{ items: AdType[] }>('/api/public/ad-types')),
    carregar_colecao('a tabela de preços', () => api_json<Pricing>('/api/public/pricing')),
    carregar_colecao('as subcategorias', () => api_json<{ items: Subcategory[] }>('/api/public/subcategories')),
    carregar_colecao('as UFs', () => api_json<{ items: StateRow[] }>('/api/public/locations/states')),
    carregar_colecao('a localização aproximada', () => api_json<GeoIp>('/api/public/geoip'))
  ])

  return {
    categories: cats.ok ? cats.data.items || [] : [],
    ad_types: types.ok ? types.data.items || [] : [],
    pricing: pricing.ok ? pricing.data : null,
    subcategories: subs.ok ? subs.data.items || [] : [],
    states: states.ok ? states.data.items || [] : [],
    geoip: geoip.ok ? geoip.data : null,
    failure: !cats.ok ? cats.failure : !types.ok ? types.failure : null
  }
}

// XHR em vez de fetch só aqui: upload de fotos precisa de progresso real.
function upload_submit_form(
  form_data: FormData,
  idempotency_key: string,
  on_progress: (pct: number) => void
): Promise<SubmitResult & { owner_token?: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/ads')
    xhr.withCredentials = true
    xhr.setRequestHeader('Idempotency-Key', idempotency_key)
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) on_progress(Math.round((event.loaded / event.total) * 100))
    }
    xhr.onload = () => {
      let body: unknown = null
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        body = { raw: xhr.responseText }
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as SubmitResult & { owner_token?: string | null })
      else reject(new ApiError(`API error (${xhr.status})`, xhr.status, body))
    }
    xhr.onerror = () => reject(new ApiError('Falha de rede ao enviar as fotos.', 0, null))
    xhr.send(form_data)
  })
}

function parse_price_to_cents(raw: string): number | null {
  const trimmed = String(raw || '').trim().replace(/[R$\s.]/g, '').replace(',', '.')
  if (!trimmed) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value < 0) return null
  return Math.round(value * 100)
}

function set_trigger_value(trigger: HTMLElement, label: string, placeholder: boolean): void {
  const label_el = trigger.querySelector<HTMLElement>('[data-trigger-label]')
  if (!label_el) return
  label_el.textContent = label
  label_el.classList.toggle('text-muted', placeholder)
  label_el.classList.toggle('text-ink', !placeholder)
}

function render_subcategory_chips(host: HTMLElement, items: Subcategory[]): void {
  if (!items.length) {
    host.innerHTML = `<span class="text-muted">Selecione uma ou mais…</span>`
    return
  }
  host.innerHTML = items.map(sc => `
    <span class="inline-flex h-7 items-center gap-1 rounded-full bg-ink/10 px-2 text-[11px] font-semibold text-ink">
      <span>${escape_html(sc.label)}</span>
      <button type="button" data-remove-sub="${sc.id}" class="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-ink/20" aria-label="Remover">${icon('x', 'h-3 w-3')}</button>
    </span>`).join('')
}

export type BindSubmitOptions = {
  deps: SubmitDependencies
  on_success: (result: SubmitResult) => void
  on_cancel?: () => void
}

export function bind_submit_form(scope: HTMLElement, opts: BindSubmitOptions): void {
  const form = scope.querySelector<HTMLFormElement>('[data-submit-form]')
  if (!form) return

  const state: FormState = {
    category: null,
    ad_type: null,
    subcategories: [],
    state: null,
    municipality: null,
    neighborhood: ''
  }

  const status_el = scope.querySelector<HTMLElement>('[data-submit-status]')!
  const button = scope.querySelector<HTMLButtonElement>('[data-submit-button]')!
  const cancel_btn = scope.querySelector<HTMLButtonElement>('[data-submit-cancel]')

  const trigger_category = scope.querySelector<HTMLButtonElement>('[data-trigger-category]')!
  const trigger_type = scope.querySelector<HTMLButtonElement>('[data-trigger-type]')!
  const trigger_subs = scope.querySelector<HTMLButtonElement>('[data-trigger-subcategories]')!
  const trigger_municipality = scope.querySelector<HTMLButtonElement>('[data-trigger-municipality]')!
  const trigger_neighborhood = scope.querySelector<HTMLButtonElement>('[data-trigger-neighborhood]')!
  const subs_host = scope.querySelector<HTMLElement>('[data-subcategory-chips]')!

  const state_select = scope.querySelector<HTMLSelectElement>('[data-state-select]')!
  const title_input = form.querySelector<HTMLInputElement>('input[name="title"]')!
  const body_input = form.querySelector<HTMLTextAreaElement>('textarea[name="body"]')!
  const title_letters_el = scope.querySelector<HTMLElement>('[data-title-letters]')!
  const body_letters_el = scope.querySelector<HTMLElement>('[data-body-letters]')!
  const cost_total_el = scope.querySelector<HTMLElement>('[data-cost-total]')!
  const cost_detail_el = scope.querySelector<HTMLElement>('[data-cost-detail]')!

  const photo_input = scope.querySelector<HTMLInputElement>('[data-photo-input]')!
  const photo_pick = scope.querySelector<HTMLButtonElement>('[data-photo-pick]')!
  const photo_grid = scope.querySelector<HTMLElement>('[data-photo-grid]')!
  const photo_counter = scope.querySelector<HTMLElement>('[data-photo-counter]')!

  const MAX_PHOTOS = 6
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024
  const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
  let photos: { file: File; preview: string }[] = []

  function show_status(kind: 'error' | 'warning', message: string) {
    status_el.className = kind === 'error'
      ? 'rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-ink'
      : 'rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-ink'
    status_el.textContent = message
  }

  function release_photos() {
    for (const photo of photos) URL.revokeObjectURL(photo.preview)
    photos = []
  }

  function render_photos() {
    photo_counter.textContent = `${photos.length} de ${MAX_PHOTOS} · arraste ou toque para escolher`
    photo_grid.classList.toggle('hidden', !photos.length)
    photo_grid.classList.toggle('grid', !!photos.length)
    photo_grid.innerHTML = photos.map((photo, index) => `
      <figure class="relative overflow-hidden rounded-md border border-border bg-surface">
        <img src="${photo.preview}" alt="Foto ${index + 1} do anúncio" class="aspect-square w-full object-cover" />
        ${index === 0 ? `<figcaption class="absolute left-1 top-1 rounded bg-black/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Capa</figcaption>` : ''}
        <div class="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/60 px-1 py-1">
          <button type="button" data-photo-left="${index}" ${index === 0 ? 'disabled' : ''} class="grid h-6 w-6 place-items-center rounded text-white hover:bg-black disabled:opacity-30" aria-label="Mover foto ${index + 1} para a esquerda">${icon('arrow-left', 'h-3.5 w-3.5')}</button>
          <button type="button" data-photo-remove="${index}" class="grid h-6 w-6 place-items-center rounded text-white hover:bg-danger" aria-label="Remover foto ${index + 1}">${icon('trash', 'h-3.5 w-3.5')}</button>
          <button type="button" data-photo-right="${index}" ${index === photos.length - 1 ? 'disabled' : ''} class="grid h-6 w-6 place-items-center rounded text-white hover:bg-black disabled:opacity-30" aria-label="Mover foto ${index + 1} para a direita">${icon('arrow-right', 'h-3.5 w-3.5')}</button>
        </div>
      </figure>`).join('')
  }

  function add_photos(files: FileList | File[] | null) {
    if (!files || !files.length) return
    const rejected: string[] = []
    for (const file of Array.from(files)) {
      if (photos.length >= MAX_PHOTOS) {
        rejected.push(`limite de ${MAX_PHOTOS} fotos atingido`)
        break
      }
      if (!PHOTO_TYPES.has(file.type)) {
        rejected.push(`"${file.name}" não é JPEG, PNG ou WebP`)
        continue
      }
      if (file.size > MAX_PHOTO_BYTES) {
        rejected.push(`"${file.name}" passa de 10 MB`)
        continue
      }
      photos.push({ file, preview: URL.createObjectURL(file) })
    }
    render_photos()
    if (rejected.length) show_status('warning', `Algumas fotos ficaram de fora: ${rejected.join('; ')}.`)
    else status_el.className = 'hidden'
  }

  photo_pick.addEventListener('click', () => photo_input.click())
  photo_input.addEventListener('change', () => {
    add_photos(photo_input.files)
    photo_input.value = ''
  })
  photo_pick.addEventListener('dragover', event => {
    event.preventDefault()
    photo_pick.classList.add('border-brand')
  })
  photo_pick.addEventListener('dragleave', () => photo_pick.classList.remove('border-brand'))
  photo_pick.addEventListener('drop', event => {
    event.preventDefault()
    photo_pick.classList.remove('border-brand')
    add_photos(event.dataTransfer?.files || null)
  })
  photo_grid.addEventListener('click', event => {
    const target = event.target as HTMLElement
    const remove = target.closest<HTMLElement>('[data-photo-remove]')
    const left = target.closest<HTMLElement>('[data-photo-left]')
    const right = target.closest<HTMLElement>('[data-photo-right]')
    if (remove) {
      const index = Number(remove.dataset.photoRemove)
      const [gone] = photos.splice(index, 1)
      if (gone) URL.revokeObjectURL(gone.preview)
    } else if (left) {
      const index = Number(left.dataset.photoLeft)
      if (index > 0) [photos[index - 1], photos[index]] = [photos[index], photos[index - 1]]
    } else if (right) {
      const index = Number(right.dataset.photoRight)
      if (index < photos.length - 1) [photos[index + 1], photos[index]] = [photos[index], photos[index + 1]]
    } else {
      return
    }
    render_photos()
  })

  function update_subcategory_trigger() {
    render_subcategory_chips(subs_host, state.subcategories)
  }

  function refresh_costs() {
    const title_letters = letter_count_of(title_input.value)
    const body_letters = letter_count_of(body_input.value)
    title_letters_el.textContent = `${title_letters} letra(s)`
    body_letters_el.textContent = `${body_letters} letra(s)`

    if (!opts.deps.pricing) {
      cost_total_el.textContent = `R$ 0,00`
      cost_detail_el.textContent = `Submissão moderada · link de gestão aparece após o envio`
      return
    }

    const pricing = opts.deps.pricing
    if (pricing.mode === 'free') {
      cost_total_el.textContent = `R$ 0,00 · gratuito nesta fase`
      cost_detail_el.textContent = `Submissão moderada · link de gestão aparece após o envio`
      return
    }

    const rate = resolve_rate(pricing, state.category?.id ?? null, state.ad_type?.id ?? null)
    const title_cost = title_letters * rate.title_cents
    const body_cost = body_letters * rate.body_cents
    const total = title_cost + body_cost
    cost_total_el.textContent = `Total: ${format_cents(total)}`
    cost_detail_el.innerHTML = `Título: ${escape_html(format_cents(title_cost))} (${title_letters}×${escape_html(format_cents(rate.title_cents))}) · Descrição: ${escape_html(format_cents(body_cost))} (${body_letters}×${escape_html(format_cents(rate.body_cents))}) · Submissão moderada`
  }

  trigger_category.addEventListener('click', () => {
    const options: ComboOption[] = opts.deps.categories.map(c => ({
      id: String(c.id),
      label: c.label,
      hint: c.code
    }))
    open_single_select_modal({
      title: 'Selecionar categoria',
      eyebrow: 'Categoria',
      placeholder: 'Buscar categoria…',
      initial: options,
      on_pick: (picked) => {
        const cat = opts.deps.categories.find(c => String(c.id) === picked.id) || null
        state.category = cat
        set_trigger_value(trigger_category, cat ? cat.label : 'Escolha a categoria…', !cat)
        if (cat) {
          state.subcategories = state.subcategories.filter(sc => sc.category_id === cat.id)
          update_subcategory_trigger()
        }
        refresh_costs()
      }
    })
  })

  trigger_type.addEventListener('click', () => {
    const options: ComboOption[] = opts.deps.ad_types.map(t => ({
      id: String(t.id),
      label: t.label,
      hint: t.code
    }))
    open_single_select_modal({
      title: 'Selecionar tipo de anúncio',
      eyebrow: 'Tipo',
      placeholder: 'Buscar tipo…',
      initial: options,
      on_pick: (picked) => {
        const ad_type = opts.deps.ad_types.find(t => String(t.id) === picked.id) || null
        state.ad_type = ad_type
        set_trigger_value(trigger_type, ad_type ? ad_type.label : 'Oferta ou procura?', !ad_type)
        refresh_costs()
      }
    })
  })

  trigger_subs.addEventListener('click', () => {
    if (!state.category) {
      status_el.className = 'rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-ink'
      status_el.textContent = 'Escolha primeiro a categoria para ver as subcategorias disponíveis.'
      return
    }
    status_el.className = 'hidden'
    const available = opts.deps.subcategories.filter(s => s.category_id === state.category!.id)
    const options: ComboOption[] = available.map(s => ({ id: String(s.id), label: s.label }))
    open_multi_select_modal({
      title: 'Selecionar subcategorias',
      eyebrow: state.category.label,
      placeholder: 'Buscar subcategoria…',
      initial: options,
      selected: state.subcategories.map(sc => ({ id: String(sc.id), label: sc.label })),
      on_done: (selected) => {
        const ids = new Set(selected.map(o => o.id))
        state.subcategories = available.filter(s => ids.has(String(s.id)))
        update_subcategory_trigger()
      }
    })
  })

  subs_host.addEventListener('click', (event) => {
    const remove = (event.target as HTMLElement).closest<HTMLElement>('[data-remove-sub]')
    if (!remove) return
    event.preventDefault()
    event.stopPropagation()
    const id = Number(remove.dataset.removeSub)
    state.subcategories = state.subcategories.filter(s => s.id !== id)
    update_subcategory_trigger()
  })

  // UF select populated synchronously
  state_select.innerHTML = `<option value="">Selecione…</option>` + opts.deps.states
    .map(s => `<option value="${s.code}">${escape_html(s.code)} — ${escape_html(s.name)}</option>`)
    .join('')

  state_select.addEventListener('change', () => {
    const code = state_select.value.toUpperCase()
    const found = opts.deps.states.find(s => s.code === code) || null
    state.state = found
    state.municipality = null
    state.neighborhood = ''
    set_trigger_value(trigger_municipality, found ? 'Buscar cidade…' : 'Selecione primeiro a UF…', true)
    set_trigger_value(trigger_neighborhood, found ? 'Selecione a cidade…' : 'Selecione primeiro a cidade…', true)
    trigger_municipality.disabled = !found
    trigger_neighborhood.disabled = true
  })

  trigger_municipality.addEventListener('click', () => {
    if (!state.state) return
    open_single_select_modal({
      title: 'Selecionar cidade',
      eyebrow: state.state.name,
      placeholder: 'Digite o nome da cidade…',
      empty_hint: 'Digite ao menos uma letra para buscar municípios desta UF.',
      search: async (query) => {
        const params = new URLSearchParams()
        params.set('state_id', String(state.state!.id))
        if (query) params.set('q', query)
        params.set('limit', '30')
        const data = await api_json<{ items: Municipality[] }>(`/api/public/locations/municipalities?${params.toString()}`)
        return (data.items || []).map(m => ({ id: String(m.id), label: m.name, hint: `${m.state_code}` }))
      },
      on_pick: (picked) => {
        const mun: Municipality = {
          id: Number(picked.id),
          state_id: state.state!.id,
          ibge_code: null,
          name: picked.label,
          state_code: state.state!.code
        }
        state.municipality = mun
        state.neighborhood = ''
        set_trigger_value(trigger_municipality, mun.name, false)
        set_trigger_value(trigger_neighborhood, 'Selecionar bairro…', true)
        trigger_neighborhood.disabled = false
      }
    })
  })

  trigger_neighborhood.addEventListener('click', () => {
    if (!state.municipality) return
    open_single_select_modal({
      title: 'Selecionar bairro',
      eyebrow: state.municipality.name,
      placeholder: 'Digite o nome do bairro…',
      empty_hint: 'Digite e selecione, ou pressione Enter para usar um bairro novo.',
      search: async (query) => {
        const params = new URLSearchParams()
        params.set('municipality_id', String(state.municipality!.id))
        if (query) params.set('q', query)
        params.set('limit', '30')
        const data = await api_json<{ items: DistrictRow[] }>(`/api/public/locations/districts?${params.toString()}`)
        return (data.items || []).map(d => ({ id: `db:${d.id}`, label: d.name, hint: d.source }))
      },
      allow_create: {
        label: 'Usar bairro digitado',
        on_create: (value) => {
          const trimmed = value.trim()
          if (!trimmed) return null
          return { id: `new:${trimmed}`, label: trimmed, hint: 'novo' }
        }
      },
      on_pick: (picked) => {
        state.neighborhood = picked.label
        set_trigger_value(trigger_neighborhood, picked.label, false)
      }
    })
  })

  // Apply GeoIP pre-selection
  if (opts.deps.geoip) {
    const geo = opts.deps.geoip
    if (geo.region_code && /^[A-Z]{2}$/.test(geo.region_code)) {
      const found = opts.deps.states.find(s => s.code === geo.region_code)
      if (found) {
        state_select.value = found.code
        state_select.dispatchEvent(new Event('change'))
        if (geo.city) {
          // Try resolve municipality by name
          void (async () => {
            try {
              const data = await api_json<{ items: Municipality[] }>(
                `/api/public/locations/municipalities?state_id=${found.id}&q=${encodeURIComponent(geo.city as string)}&limit=5`
              )
              const exact = (data.items || []).find(m =>
                m.name.toLowerCase() === String(geo.city).toLowerCase()
              )
              if (exact) {
                state.municipality = exact
                set_trigger_value(trigger_municipality, exact.name, false)
                trigger_neighborhood.disabled = false
                set_trigger_value(trigger_neighborhood, 'Selecionar bairro…', true)
              } else if (data.items && data.items.length === 1) {
                state.municipality = data.items[0]
                set_trigger_value(trigger_municipality, data.items[0].name, false)
                trigger_neighborhood.disabled = false
                set_trigger_value(trigger_neighborhood, 'Selecionar bairro…', true)
              }
              // Se GeoIP "city" nao casou municipio (provavel RA do DF), mapeia
              // pra Brasilia + bairro = city. Mas se city == Brasília, nao
              // duplicar como bairro.
              if (!state.municipality && found.code === 'DF') {
                const brasilia = (await api_json<{ items: Municipality[] }>(
                  `/api/public/locations/municipalities?state_id=${found.id}&q=Bras%C3%ADlia&limit=2`
                )).items?.[0]
                if (brasilia) {
                  state.municipality = brasilia
                  set_trigger_value(trigger_municipality, brasilia.name, false)
                  trigger_neighborhood.disabled = false
                  const raw_city = String(geo.city).trim()
                  if (raw_city.toLowerCase() !== brasilia.name.toLowerCase()) {
                    state.neighborhood = raw_city
                    set_trigger_value(trigger_neighborhood, raw_city, false)
                  } else {
                    set_trigger_value(trigger_neighborhood, 'Selecionar bairro…', true)
                  }
                }
              }
            } catch {
              // ignore failed pre-fill
            }
          })()
        }
      }
    }
  }

  if (cancel_btn && opts.on_cancel) {
    cancel_btn.addEventListener('click', () => opts.on_cancel?.())
  }

  form.addEventListener('input', refresh_costs)
  update_subcategory_trigger()
  refresh_costs()

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    if (!state.category) {
      status_el.className = 'rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-ink'
      status_el.textContent = 'Selecione a categoria do anúncio.'
      return
    }
    if (!state.ad_type) {
      status_el.className = 'rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-ink'
      status_el.textContent = 'Selecione o tipo do anúncio (oferta, procura...).'
      return
    }

    const data = new FormData(form)

    // Decisão 4 (F5.1): pelo menos um canal. Checar aqui evita a viagem e diz
    // ONDE clicar; a API recusa igual, com `contact_required`. É a MESMA regra,
    // não uma paralela — o backend continua sendo quem decide.
    if (!tem_canal_de_contato(String(data.get('contact_email') || ''), String(data.get('contact_phone') || ''))) {
      status_el.className = 'rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-ink'
      status_el.textContent =
        'Informe um telefone com DDD ou um email — é por onde os interessados vão falar com você.'
      form.querySelector<HTMLInputElement>('input[name="contact_phone"]')?.focus()
      return
    }

    const idempotency_key = crypto.randomUUID()
    const price_cents = parse_price_to_cents(String(data.get('price_brl') || ''))

    button.disabled = true
    const original_label = button.innerHTML
    button.innerHTML = '<span class="text-xs">Enviando…</span>'
    status_el.className = 'hidden'

    const payload = {
      category_code: state.category.code,
      type_code: state.ad_type.code,
      type: state.ad_type.code,
      title: String(data.get('title') || '').trim(),
      body: String(data.get('body') || '').trim(),
      subcategory_ids: state.subcategories.map(s => s.id),
      state_uf: state.state?.code || null,
      state_id: state.state?.id || null,
      municipality_id: state.municipality?.id || null,
      city: state.municipality?.name || null,
      neighborhood: state.neighborhood || null,
      price_cents,
      contact_name: String(data.get('contact_name') || '').trim() || null,
      contact_phone: String(data.get('contact_phone') || '').trim() || null,
      contact_email: String(data.get('contact_email') || '').trim() || null,
      contact_form_enabled: !!data.get('contact_form_enabled')
    }

    try {
      // Token de uso único; o backend rejeita sem ele (fail closed).
      const captcha_token = await get_turnstile_token()

      let result: SubmitResult & { owner_token?: string | null }
      if (photos.length) {
        const form_data = new FormData()
        for (const [key, value] of Object.entries(payload)) {
          if (value == null) continue
          if (key === 'subcategory_ids') form_data.set(key, (value as number[]).join(','))
          else form_data.set(key, String(value))
        }
        if (captcha_token) form_data.set('cf-turnstile-response', captcha_token)
        for (const photo of photos) form_data.append('photos', photo.file, photo.file.name)
        result = await upload_submit_form(form_data, idempotency_key, pct => {
          button.innerHTML = `<span class="text-xs">Enviando fotos… ${pct}%</span>`
        })
      } else {
        result = await api_json<SubmitResult & { owner_token?: string | null }>('/api/ads', {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotency_key },
          body: JSON.stringify({ ...payload, 'cf-turnstile-response': captcha_token })
        })
      }
      release_photos()
      opts.on_success(result)
    } catch (err) {
      button.disabled = false
      button.innerHTML = original_label
      // Indisponibilidade do CAPTCHA (config que não veio, script bloqueado por
      // adblock) é diferente de desafio não concluído. O primeiro não é culpa de
      // quem está enviando e não pede recarregar a página — pede tentar de novo.
      show_status('error', submit_error_message(err))
    }
  })
}
